import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Langs, { Localize } from "../../app/lang/langs";
import { checkPermission } from "../../app/utils/helpers";
import { config } from "../../app/utils/config";
import {
  setCollabModalData,
  setSelectedView,
} from "../../components/collabs/collabsSlice";
import CollabModalDetails from "../collabs/CollabModalDetails";
import MobileCollabModalDetails from "../collabs/mobile/MobileCollabModalDetails";
import WeeklyFollowupModal from "../collabs/WeeklyFollowupModal";
import ContractsTopMenu from "./ContractsTopMenu";
import ContractsDataTable from "./ContractsDataTable";
import MissionsDataTable from "./MissionsDataTable";
import MissionsSuivisDataTable from "./MissionsSuivisDataTable";
import MobileContractsTopMenu from "./mobile/MobileContractsTopMenu";
import MobileContractsList from "./mobile/MobileContractsList";
import MobileMissionsList from "./mobile/MobileMissionsList";
import MobileMissionsSuivisList from "./mobile/MobileMissionsSuivisList";
import MobileFollowupDrawer from "./mobile/MobileFollowupDrawer";
import { useContractsData } from "./useContractsData";
import { useContractsFilters } from "./useContractsFilters";
import {
  exportExcelContracts,
  exportExcelMissions,
  exportExcelSuivisMissions,
} from "./contractsExcelExport";
import { getScrollHeight } from "./contractsTableUtils";
import styles from "./ContractsView.module.css";

export function ContractsView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // --- Redux State ---
  const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds);
  const allCollabsList = useSelector((state) => state.collabs.allCollabsList);
  const allMissionsList = useSelector((state) => state.teams.allMissions);
  const allMissionsFollowups = useSelector((state) => state.collabs.allMissionsFollowups);
  const isMobile = useSelector((state) => state.settings.isMobile);
  const allContractsList = useSelector((state) => state.collabs.allContracts);
  const selectedView = useSelector((state) => state.collabs.selectedView);

  // --- Local State ---
  const [selectedFollowup, setSelectedFollowup] = useState();
  const [filteredContracts, setFilteredContracts] = useState(null);
  const [filteredMissions, setFilteredMissions] = useState(null);
  const [filteredMissionsSuivis, setFilteredMissionsSuivis] = useState(null);

  const [viewsOptions] = useState([
    { label: <Langs str="Contracts" />, value: 0 },
    { label: <Langs str="Missions" />, value: 1 },
    { label: <Langs str="Suivis Missions" />, value: 2 },
  ]);

  // --- Custom Hooks ---
  useContractsData(viewsOptions);

  const {
    collabsFilters,
    missionsFilters,
    missionsSuivisFilters,
    collabsGlobalFilterValue,
    missionsGlobalFilterValue,
    missionsSuivisGlobalFilterValue,
    onCollabsGlobalFilterChange,
    onMissionsGlobalFilterChange,
    onMissionsSuivisGlobalFilterChange,
  } = useContractsFilters();

  // --- Derived Data ---
  let companies = [],
    statuses = [],
    collabsTypes = [],
    followupTypes = [],
    followupStatuses = [];

  allCollabsList.map((collab) => {
    if (collab.company && !companies.includes(collab.company))
      companies.push(collab.company);
    if (collab.statut && !statuses.includes(collab.statut))
      statuses.push(collab.statut);
    if (collab.type_collab && !collabsTypes.includes(collab.type_collab))
      collabsTypes.push(collab.type_collab);
  });

  allMissionsFollowups.map((f) => {
    if (f.status && !followupStatuses.includes(f.status))
      followupStatuses.push(f.status);
    if (f.type && !followupTypes.includes(f.type))
      followupTypes.push(f.type);
  });

  // --- Handlers ---
  const handleViewChange = (view) => {
    if (view != null) {
      let selectedTeamId = selectedTeamsIds[0];
      navigate(`/contracts/${selectedTeamId}/${view}`);
    }
  };

  function viewCollabModal(data) {
    if (!checkPermission(config.scopes.collabs, config.permissionTypes.edit))
      return;
    const modalData = {
      ...data,
      show: true,
      tabIndex: selectedView == 1 ? 0 : 1,
    };
    dispatch(setCollabModalData(modalData));
  }

  function viewReviewModal(data) {
    setSelectedFollowup(data);
  }

  const navToTeam = (rowData) => {
    const encodedLabel = encodeURIComponent(rowData.current_team_name);
    navigate(`/clients/${selectedTeamsIds[0]}/1/${encodedLabel}`);
  };

  const teamBodyTemplate = (rowData) => {
    return (
      <a className={styles.linkStyle} onClick={() => navToTeam(rowData)}>
        {rowData.current_team_name}
      </a>
    );
  };

  // --- Scroll Height ---
  const scrollHeight = getScrollHeight(isMobile);

  // --- Render Body (Desktop) ---
  function renderBody() {
    let subView = viewsOptions[selectedView];
    if (!subView) return null;

    switch (subView.value) {
      case 0:
        return (
          <ContractsDataTable
            data={allCollabsList}
            filters={collabsFilters}
            globalFilterValue={collabsGlobalFilterValue}
            onGlobalFilterChange={onCollabsGlobalFilterChange}
            onRowDoubleClick={(e) => viewCollabModal(e.data)}
            onValueChange={(data) => setFilteredContracts(data)}
            scrollHeight={scrollHeight}
            statuses={statuses}
            companies={companies}
            collabsTypes={collabsTypes}
          />
        );
      case 1:
        return (
          <MissionsDataTable
            data={allMissionsList}
            filters={missionsFilters}
            globalFilterValue={missionsGlobalFilterValue}
            onGlobalFilterChange={onMissionsGlobalFilterChange}
            onRowDoubleClick={(e) => viewCollabModal(e.data)}
            onValueChange={(data) => setFilteredMissions(data)}
            scrollHeight={scrollHeight}
            statuses={statuses}
            teamBodyTemplate={teamBodyTemplate}
          />
        );
      case 2:
        return (
          <MissionsSuivisDataTable
            data={allMissionsFollowups}
            filters={missionsSuivisFilters}
            globalFilterValue={missionsSuivisGlobalFilterValue}
            onGlobalFilterChange={onMissionsSuivisGlobalFilterChange}
            onRowDoubleClick={(e) => viewReviewModal(e.data)}
            onValueChange={(data) => setFilteredMissionsSuivis(data)}
            scrollHeight={scrollHeight}
            followupStatuses={followupStatuses}
            followupTypes={followupTypes}
            teamBodyTemplate={teamBodyTemplate}
          />
        );
      default:
        return null;
    }
  }

  // --- Render Body (Mobile) ---
  function renderMobileBody() {
    let subView = viewsOptions[selectedView];
    if (!subView) return null;

    switch (subView.value) {
      case 0:
        return (
          <MobileContractsList
            data={allCollabsList}
            onCardTap={(data) => viewCollabModal(data)}
            onValueChange={(data) => setFilteredContracts(data)}
          />
        );
      case 1:
        return (
          <MobileMissionsList
            data={allMissionsList}
            onCardTap={(data) => viewCollabModal(data)}
            onValueChange={(data) => setFilteredMissions(data)}
            onTeamTap={navToTeam}
          />
        );
      case 2:
        return (
          <MobileMissionsSuivisList
            data={allMissionsFollowups}
            onCardTap={(data) => viewReviewModal(data)}
            onValueChange={(data) => setFilteredMissionsSuivis(data)}
            onTeamTap={navToTeam}
          />
        );
      default:
        return null;
    }
  }

  // --- Mobile render ---
  const prevViewRef = useRef(selectedView);
  const [mobileBodyKey, setMobileBodyKey] = useState(0);

  useEffect(() => {
    if (prevViewRef.current !== selectedView) {
      setMobileBodyKey((k) => k + 1);
      prevViewRef.current = selectedView;
    }
  }, [selectedView]);

  if (isMobile === 2) {
    return (
      <div
        className={styles.containerView}
        style={{
          background: "linear-gradient(180deg, rgba(0,40,128,0.02) 0%, rgba(255,144,21,0.015) 100%)",
          minHeight: "100vh",
        }}
      >
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            /* Let MobileContractsTopMenu's own glassmorphism show through */
            backgroundColor: "transparent",
          }}
        >
          <MobileContractsTopMenu
            viewsOptions={viewsOptions}
            selectedView={selectedView}
            onViewChange={handleViewChange}
            onExportContracts={() =>
              exportExcelContracts(filteredContracts, allContractsList)
            }
            onExportMissions={() =>
              exportExcelMissions(filteredMissions, allMissionsList)
            }
            onExportSuivisMissions={() =>
              exportExcelSuivisMissions(filteredMissionsSuivis, allMissionsFollowups)
            }
          />
        </div>

        {/* Body content with fade/slide animation on view switch */}
        <div
          key={mobileBodyKey}
          style={{
            paddingTop: 0,
            paddingBottom: 24,
            paddingLeft: 4,
            paddingRight: 4,
            animation: "contractsMobileFadeSlideIn 0.32s cubic-bezier(0.4, 0, 0.2, 1) both",
          }}
        >
          {renderMobileBody()}
        </div>

        <MobileCollabModalDetails
          page="collabs"
          tabs={["contrat", "societe", "mission", "donneesPerso", "profil"]}
        />
        <MobileFollowupDrawer
          selected={selectedFollowup}
          onHide={() => setSelectedFollowup(null)}
        />

        {/* Scoped keyframes for mobile view transition */}
        <style>{`
          @keyframes contractsMobileFadeSlideIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </div>
    );
  }

  // --- Desktop render ---
  return (
    <div className={styles.containerView}>
      <div className="topMenu">
        <ContractsTopMenu
          isMobile={isMobile}
          selectedView={selectedView}
          viewsOptions={viewsOptions}
          onViewChange={handleViewChange}
          onExportContracts={() =>
            exportExcelContracts(filteredContracts, allContractsList)
          }
          onExportMissions={() =>
            exportExcelMissions(filteredMissions, allMissionsList)
          }
          onExportSuivisMissions={() =>
            exportExcelSuivisMissions(filteredMissionsSuivis, allMissionsFollowups)
          }
        />
      </div>
      {renderBody()}
      <CollabModalDetails
        page="collabs"
        tabs={["contrat", "societe", "mission", "donneesPerso", "profil"]}
      />
      <WeeklyFollowupModal
        selected={selectedFollowup}
        onHide={() => setSelectedFollowup(null)}
      />
    </div>
  );
}
